import { useRouter } from 'next/router';
import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import invariant from 'tiny-invariant';
import config from '../config';
import { ogCityImageUrl } from '../config/cityUpgrades';
import { getCity } from '../firebase-client';
import cacheCity from '../utils/cacheCity';
import checkWallets from '../utils/checkWallets';
import defaultBlocksInCity from '../utils/defaultBlocksInCity';
import updateCity from '../utils/updateCity';
import { useAlgo } from './useAlgo';
import { useBlocksStore } from './useBlocksStore';
import { useCitiesStore } from './useCitiesStore';

const CityBuilderContext = createContext({});
export const useCityBuilder = () => useContext(CityBuilderContext);

export const CityBuilderProvider = ({ children }) => {
  const router = useRouter();
  const { allBlocks, myBlocks } = useBlocksStore();
  const { allCities } = useCitiesStore();
  const [uiBlocks, setUiBlocks] = useState(myBlocks);
  const { sendBlocksInitiate, signAndSendBlocksConfirm, address } = useAlgo();
  const [selectedCity, setSelectedCity] = useState(null);
  const [originalBlocksInCity, setOriginalBlocksInCity] = useState(defaultBlocksInCity);
  const [blocksInCity, setBlocksInCity] = useState(defaultBlocksInCity);
  const [originalUpgrade, setOriginalUpgrade] = useState(null);
  const [currentUpgrade, setCurrentUpgrade] = useState(null);

  const originalUpgradeUrl = !originalUpgrade ? ogCityImageUrl : originalUpgrade?.imageUrlClient;
  const currentUpgradeUrl = !currentUpgrade ? ogCityImageUrl : currentUpgrade?.imageUrlClient;

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [hasClickedConfirm, setHasClickedConfirm] = useState(false);
  const [numberOfTxns, setNumberOfTxns] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [txnsToConfirm, setTxnsToConfirm] = useState(null);

  const steps = [
    { id: 0, name: 'Select your Shitty City', onClick: () => setActiveStep(0) },
    {
      id: 1,
      name: 'Update Shitty City',
      onClick: () => {
        if (selectedCity) {
          setActiveStep(1);
        }
      },
    },
    {
      id: 2,
      name: 'Confirm Updates',
      onClick: () => {
        if (selectedCity) {
          setActiveStep(2);
        }
      },
    },
  ];

  const selectCity = async (city) => {
    if (!city) return;
    await cacheCity(city.index);
    const theCity = await getCity(city.index, allCities);
    setSelectedCity(theCity);
    setActiveStep(1);
    setTxnsToConfirm(null);
  };

  const removeAllBlocks = () => {
    const updatedBlocks = [...blocksInCity].map((block) => {
      return { ...block, block: null };
    });
    setBlocksInCity(updatedBlocks);
  };

  const addRandomBlocks = () => {
    // get 9 random blocks from myBlocks or how max blocks in myBlocks
    const randomBlocks = [];
    const maxBlocks = myBlocks.length;
    const max = myBlocks.length > 9 ? 9 : myBlocks.length;
    for (let i = 0; i < max; i++) {
      const randomIndex = Math.floor(Math.random() * maxBlocks);
      const weight = defaultBlocksInCity.find((block) => block.index === i + 1).weight;
      randomBlocks.push({ index: i + 1, block: myBlocks[randomIndex], weight });
    }
    setBlocksInCity(randomBlocks);
  };

  const resetCity = () => {
    if (!selectedCity) return;
    setBlocksInCity(originalBlocksInCity);
    setCurrentUpgrade(originalUpgrade);
  };

  const saveCity = () => {
    if (!selectedCity) return;
    if (JSON.stringify(originalBlocksInCity) === JSON.stringify(blocksInCity) && JSON.stringify(originalUpgrade) === JSON.stringify(currentUpgrade)) {
      return toast.error('No changes made');
    }
    if (process.env.NEXT_PUBLIC_CITY_UPGRADES_ARE_LIVE !== 'true' && currentUpgrade) {
      return toast.error('City Upgrades are not live yet');
    }
    if (numberOfTxns > 16) {
      return toast.error('Too many transactions. Please revert some changes and try again.');
    }
    setActiveStep(2);
    setTxnsToConfirm(null);
  };

  const initiateConfirmCity = async () => {
    invariant(address, 'No address provided');

    try {
      if (
        JSON.stringify(originalBlocksInCity) === JSON.stringify(blocksInCity) &&
        JSON.stringify(originalUpgrade) === JSON.stringify(currentUpgrade)
      ) {
        return toast.error('No changes made');
      }
      setConfirmLoading(true);
      const initiateTxns = await sendBlocksInitiate(
        selectedCity.index,
        selectedCity.cid,
        originalBlocksInCity,
        blocksInCity,
        originalUpgrade,
        currentUpgrade
      );
      const txns = await updateCity(initiateTxns);
      setTxnsToConfirm(txns);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data || error.message);
    } finally {
      setConfirmLoading(false);
    }
  };

  const confirmCity = async () => {
    invariant(address, 'No address provided');
    invariant(txnsToConfirm, 'No txns to confirm');

    try {
      setConfirmLoading(true);
      setHasClickedConfirm(true);
      await signAndSendBlocksConfirm(txnsToConfirm);
      try {
        checkWallets(address);
      } catch (error) {
        console.error(error);
      }
      toast.success(`Successfully updated ${selectedCity.params.name}. Refresh page to see changes.`);
      location.href = `/cities/${selectedCity.index}`;
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data || error.message || 'Something went wrong');
      setConfirmLoading(false);
      setHasClickedConfirm(false);
    }
  };

  const resetFlow = () => {
    setSelectedCity(null);
    setBlocksInCity(defaultBlocksInCity);
    setOriginalBlocksInCity(defaultBlocksInCity);
    setOriginalUpgrade(null);
    setCurrentUpgrade(null);
    setActiveStep(0);
    setTxnsToConfirm(null);
    setHasClickedConfirm(false);
  };

  // * setup local blocks
  useEffect(() => {
    if (myBlocks.length === 0) return;
    setUiBlocks(myBlocks);
  }, [JSON.stringify(myBlocks)]);

  // * update initial blocks in city when someone selects a city
  useEffect(() => {
    if (!selectedCity || allBlocks.length === 0) return;
    const meta = selectedCity.metadata?.properties;

    // update blocksInCity
    const updatedBlocks = [...blocksInCity].map((block) => {
      if (!meta?.[`Block #${block.index}`]) return block;
      const id = meta?.[`Block #${block.index}`].split(' - ')[1];
      if (!id) return block;
      const blockData = allBlocks.find((block) => block.index === parseInt(id));
      return { ...block, block: blockData };
    });

    setBlocksInCity(updatedBlocks);
    setOriginalBlocksInCity(updatedBlocks);

    // get Upgrade metadata
    const _upgrade = meta?.Upgrade;
    const upgradeDetails = config.cityUpgrades.find((upgrade) => upgrade.name === _upgrade);
    setOriginalUpgrade(_upgrade === 'None' ? null : upgradeDetails);
    setCurrentUpgrade(_upgrade === 'None' ? null : upgradeDetails);
  }, [allBlocks, selectedCity]);

  useEffect(() => {
    if (!selectedCity) return;
    const addedBlocks = blocksInCity
      .filter((block) => {
        const originalBlock = originalBlocksInCity.find((b) => b.index === block.index);
        return originalBlock?.block?.index !== block?.block?.index;
      })
      .filter((block) => !!block)
      .map((block) => block?.block?.index)
      .filter((block) => !!block);
    const removedBlocks = originalBlocksInCity
      .filter((block) => {
        const updatedBlock = blocksInCity.find((b) => b.index === block.index);
        return updatedBlock?.block?.index !== block?.block?.index;
      })
      .filter((block) => !!block)
      .map((block) => block?.block?.index)
      .filter((block) => !!block);

    const addedTxns = addedBlocks.length;
    const removedTxns = removedBlocks.length * 2;
    const upgradeTxn = originalUpgrade?.name === currentUpgrade?.name ? 0 : 2;
    const totalTxns = addedTxns + removedTxns + upgradeTxn;
    setNumberOfTxns(totalTxns + 1); // add 1 for config txn
  }, [originalBlocksInCity, blocksInCity]);

  return (
    <CityBuilderContext.Provider
      value={{
        steps,
        activeStep,
        selectCity,
        saveCity,
        removeAllBlocks,
        confirmCity,
        confirmLoading,
        initiateConfirmCity,
        txnsToConfirm,
        selectedCity,
        setSelectedCity,
        blocksInCity,
        setBlocksInCity,
        originalBlocksInCity,
        uiBlocks,
        setUiBlocks,
        addRandomBlocks,
        resetCity,
        numberOfTxns,
        resetFlow,
        hasClickedConfirm,
        originalUpgrade,
        currentUpgrade,
        setCurrentUpgrade,
        currentUpgradeUrl,
        originalUpgradeUrl,
      }}
    >
      {children}
    </CityBuilderContext.Provider>
  );
};
